import { Box, Paper, styled, Typography } from '@mui/material';
import JobDTO from '@src/adapters/dto/job/JobDTO';
import { formatDate } from '@src/utils/dateUtils';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import Email from '@mui/icons-material/Email';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import DateRange from '@mui/icons-material/DateRange';
import PhoneEnabled from '@mui/icons-material/PhoneEnabled';
import DepartmentName from '@src/components/department/DepartmentName';
import ProductionCompany from '@src/components/productionCompany/ProductionCompany';
import Production from '@src/components/production/Production';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

interface Props {
  job: JobDTO;
}

const StyledTitle = styled(Typography)(({ theme }) => ({
  variant: 'body1',
  color: theme.palette.text.secondary,
}));

const StyledHighlightedTitle = styled(Typography)(({ theme }) => ({
  variant: 'body1',
  color: theme.palette.primary.dark,
}));

const StyledContent = styled(Typography)(({ theme }) => ({
  variant: 'body1',
  color: theme.palette.text.primary,
}));

const StyledSection = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(4),
}));

const StyledSubContent = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4.5),
}));

const StyledIconContainer = styled(Box)(({ theme }) => ({
  paddingRight: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
}));

function Information({ job }: Props) {
  return (
    <Paper elevation={8}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <StyledSection>
            <StyledTitle>Production Company</StyledTitle>
            <StyledHighlightedTitle>
              <ProductionCompany id={job.productionCompanyId} />
            </StyledHighlightedTitle>
          </StyledSection>
          <Typography
            variant="h6"
            sx={(theme) => ({
              height: theme.spacing(3),
              marginBottom: theme.spacing(1.5),
            })}>
            Department Contact
          </Typography>
          <Box>
            <StyledTitle>Department head</StyledTitle>
            <StyledHighlightedTitle>{job.contact.name}</StyledHighlightedTitle>
          </Box>
          <StyledSection>
            <StyledTitle>Location</StyledTitle>
            <StyledContent>{job.location}</StyledContent>
          </StyledSection>
        </Box>
        <Box>
          <StyledSection sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <VideocamOutlinedIcon
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <StyledTitle>Production</StyledTitle>
              <StyledContent>
                <Production id={job.productionId} />
              </StyledContent>
            </Box>
          </StyledSection>
          <StyledSubContent sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <Email
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <StyledTitle>Email</StyledTitle>
              <StyledContent>{job.contact.email}</StyledContent>
            </Box>
          </StyledSubContent>
          <StyledSection>
            <StyledTitle>Contract Type</StyledTitle>
            <StyledContent>{job.contractType}</StyledContent>
          </StyledSection>
        </Box>
        <Box>
          <StyledSection sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <WorkOutlineIcon
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <StyledTitle>Department</StyledTitle>
              <StyledContent>
                <DepartmentName id={job.departmentId} />
              </StyledContent>
            </Box>
          </StyledSection>
          <StyledSubContent sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <PhoneEnabled
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <Typography
                variant="body1"
                sx={{ color: (theme) => theme.palette.text.secondary }}>
                Phone
              </Typography>
              <StyledContent>{job.contact.phone}</StyledContent>
            </Box>
          </StyledSubContent>
          <StyledSection>
            <StyledTitle>Additional Information</StyledTitle>
            <StyledContent>{job.additionalInfo}</StyledContent>
          </StyledSection>
        </Box>
        <Box>
          <StyledSection sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <DateRange
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <StyledTitle>Start Date</StyledTitle>
              <StyledContent>
                {job.startDate && formatDate(job.startDate, 'DD MMM YYYY')}
              </StyledContent>
            </Box>
          </StyledSection>
          <Box>
            <StyledSubContent sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledIconContainer>
                <CurrencyPoundIcon
                  sx={(theme) => ({ color: theme.palette.text.secondary })}
                />
              </StyledIconContainer>
              <Box>
                <StyledTitle>Fee</StyledTitle>
                <StyledContent>{job.fee}</StyledContent>
              </Box>
            </StyledSubContent>
          </Box>
        </Box>
        <Box>
          <StyledSection sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <DateRange
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <StyledTitle>End Date</StyledTitle>
              <StyledContent>
                {job.endDate && formatDate(job.endDate, 'DD MMM YYYY')}
              </StyledContent>
            </Box>
          </StyledSection>
          <StyledSubContent sx={{ display: 'flex', alignItems: 'center' }}>
            <StyledIconContainer>
              <PersonOutlineOutlinedIcon
                sx={(theme) => ({ color: theme.palette.text.secondary })}
              />
            </StyledIconContainer>
            <Box>
              <Typography
                variant="body1"
                sx={{ color: (theme) => theme.palette.text.secondary }}>
                Number of Positions
              </Typography>
              <StyledContent>{job.numberOfPositions}</StyledContent>
            </Box>
          </StyledSubContent>
        </Box>
      </Box>
    </Paper>
  );
}

export default Information;
